import React, { useCallback, useContext, useState, useMemo } from "react";
import { Provider } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import useHeapIdentify from "../hooks/useHeapIdentify";

import Header from "../components/header/Header";
import SideNav from "../components/sidenav/SideNav";
import ActionCenterPopover from "../components/sidenav/ActionCenterPopover";
import MaintenanceModal from "../components/maintenance-modal/MaintenanceModal";
import { useActionCenterData } from "../hooks/useActionCenterData";
import { useActionCenterV2Data } from "../hooks/useActionCenterV2Data";
import {
  ActionCenterProvider,
  useActionCenter,
} from "../contexts/ActionCenterContext";
import { usePermission, P } from "../rbac";

import {
  PropertyStoreContext,
  PropertyStoreProvider,
} from "../contexts/PropertyStoreContext";
import { PermissionProvider } from "../contexts/PermissionContext";
import PermissionLoader from "../rbac/components/PermissionLoader";
import { UserManagementBridge } from "../userManagementConfig";
import { OFFLINE_CRS_ID } from "../constants";
import { store } from "./../store";

const AppLayout = ({ isOpen, toggleSidebar }) => {
  const { selectedProperty } = useContext(PropertyStoreContext);
  const location = useLocation();

  // Initialize Heap tracking - now has access to PropertyStoreContext
  useHeapIdentify();

  // Memoize derived value to prevent unnecessary re-renders
  const isOffline = useMemo(
    () => selectedProperty?.crsId === OFFLINE_CRS_ID,
    [selectedProperty?.crsId]
  );

  // Recommendation data drives the Home nav badge count and the once-per-
  // property-per-session Action Center popover on non-home pages. Both point
  // at the homepage Action Center, so roles without homepage access (e.g.
  // supplier Reservation/Finance) get neither the popover nor the fetch.
  const { allowed: canViewHome } = usePermission(P.Home.View);
  const { actionCenterData, actionCenterSettled } = useActionCenterData(
    canViewHome ? selectedProperty : null
  );
  // The v2 (LLM) cards render into the same Action Center, so the badge counts them
  // too — otherwise it disagrees with the count in the section header. Both hooks
  // dedupe their in-flight request with the Performance page's copy, so mounting
  // them here does not double the fetch.
  const { actionCenterV2Data, actionCenterV2Settled } = useActionCenterV2Data(
    canViewHome ? selectedProperty : null
  );
  // The badge waits for BOTH feeds to settle (success or failure) before showing
  // anything (EO-1834) — the gating lives in ActionCenterProvider, which also
  // derives the count from the same buildActionCards the page renders from, so
  // the badge and the page header are always the same number (EO-1907).
  const feedsSettled = actionCenterSettled && actionCenterV2Settled;

  return (
    <ActionCenterProvider
      propertyId={selectedProperty?.propertyId}
      data={actionCenterData}
      v2Data={actionCenterV2Data}
      feedsSettled={feedsSettled}
    >
      <div className="layout">
        <div className="layout__main">
          <SideNavWithBadge isOpen={isOpen} isOffline={isOffline} />
          {canViewHome && (
            <ActionCenterPopover
              data={actionCenterData}
              v2Data={actionCenterV2Data}
              propertyId={selectedProperty?.propertyId}
              isSidebarOpen={isOpen}
            />
          )}

          <div className="layout__content">
            <div className="layout__content-section">
              <div className="layout__header">
                <Header
                  isSidebarOpen={isOpen}
                  onToggleSidebar={toggleSidebar}
                />
              </div>

              <div className="layout__content-wrapper">
                <main className="layout__content-main">
                  {selectedProperty || location.pathname === "/unauthorized" ? (
                    <Outlet />
                  ) : (
                    <p style={{ margin: "40px 60px" }}>Select a property</p>
                  )}
                </main>

                <MaintenanceModal />
              </div>
            </div>
          </div>
        </div>
      </div>
    </ActionCenterProvider>
  );
};

// Reads the recommendation count from the provider so the badge stays in lockstep
// with the Action Center page header. Kept as a thin wrapper so SideNav itself has
// no dependency on the Action Center context.
const SideNavWithBadge = ({ isOpen, isOffline }) => {
  const { recommendationCount } = useActionCenter();
  return (
    <SideNav
      isOpen={isOpen}
      isOffline={isOffline}
      recommendationCount={recommendationCount}
    />
  );
};

SideNavWithBadge.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isOffline: PropTypes.bool.isRequired,
};

const RootLayout = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleSidebar = useCallback(() => setIsOpen((v) => !v), []);

  return (
    <div className="App">
      <Provider store={store}>
        <PropertyStoreProvider>
          <PermissionProvider>
            <PermissionLoader>
              <UserManagementBridge>
                <AppLayout isOpen={isOpen} toggleSidebar={toggleSidebar} />
              </UserManagementBridge>
            </PermissionLoader>
          </PermissionProvider>
        </PropertyStoreProvider>
      </Provider>
    </div>
  );
};

AppLayout.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggleSidebar: PropTypes.func.isRequired,
};

export default RootLayout;
